<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { FieldScript } from '@/components/forms/properties';
import { ScriptTypeEnum } from '@/modules/low-code/services/ScriptsService';
import { TriggerModel, TriggerModes, TriggerMoments } from '@/components/builder/form/traits/HasTriggers';

const model = defineModel<TriggerModel[]>();

const props = defineProps({
  "mode": null,
  "blueprint": null
});

const route = useRoute();
const triggers = ref<TriggerModel[]>(model.value ?? []);

const scriptType = computed(() => props.mode === TriggerModes.ServerSide ? ScriptTypeEnum.DynamicTrigger : ScriptTypeEnum.DynamicClientSideTrigger);
const moduleId = computed(() => route.params.moduleId.toString());
const triggerMoments = computed(() =>
{
    const triggerMomentsArray = Object.entries(TriggerMoments).map(([key, value]) => ({
        key,
        value
    }));

    if (props.mode === TriggerModes.ServerSide)
        return triggerMomentsArray.filter(p => props.blueprint.serverSideTriggerMoments.includes(p.key));

    if (props.mode === TriggerModes.ClientSide)
        return triggerMomentsArray.filter(p => props.blueprint.clientSideTriggerMoments.includes(p.key));

    return [];
});

watch(model, (val) =>
{
    triggers.value = val ?? [];
}, { immediate: true });

watch(triggers, (val) =>
{
    model.value = val;
}, { deep: true });

const toggleExpanded = (trigger: TriggerModel) =>
{
    trigger.expanded = !trigger.expanded;
};

const addTrigger = (triggers: TriggerModel[]) =>
{
    triggers.push({
        id: crypto.randomUUID(),
        expanded: true,
        moment: null,
        script: null,
        scriptConfigId: null,
    });
};

const removeTrigger = (triggers: TriggerModel[], trigger: TriggerModel) =>
{
    triggers.remove(p => p.id === trigger.id);
};

const updateScriptConfig = (id: string, trigger: TriggerModel) =>
{
    trigger.scriptConfigId = id;
};
</script>

<template>
    <div class="mb-2">
        <div class="section-header">
            <h4>{{ mode === TriggerModes.ServerSide ? $t('[[[Server side]]]') : $t('Client side') }}</h4>
            <button type="button" class="add-trigger" @click="addTrigger(triggers)">
                <i class="fa-solid fa-plus"></i>
            </button>
        </div>

        <div v-if="triggers.length > 0">
            <div
                v-for="(trigger, index) in triggers"
                :key="trigger.id"
            >
                <div class="trigger-header">
                    <div class="summary" @click="toggleExpanded(trigger)">
                        <strong>{{ trigger.moment?.value ?? '—' }}</strong> •
                        {{ trigger.script?.value || $t('—') }}
                    </div>
                    <div class="actions">
                        <button type="button" class="delete-trigger" @click="removeTrigger(triggers, trigger)">
                            <i class="fa-solid fa-trash"></i>
                        </button>
                    </div>
                </div>

                <div v-if="trigger.expanded">
                    <ideo-form-group
                        :label="$t('[[[Moment wykonania]]]')"
                        required="true"
                    >
                        <IdeoSelect
                            v-model="trigger.moment"
                            :options="triggerMoments"
                        />
                    </ideo-form-group>
                    <ideo-form-group
                        :label="$t('[[[Skrypt]]]')"
                        required="true"
                    >
                        <FieldScript
                            v-model="trigger.script"
                            :script-config-id="trigger.scriptConfigId"
                            :show-label="false"
                            :label="$t('[[[Wybierz skrypt]]]')"
                            :endpoint="`admin/forms/scripts`"
                            :script-type="scriptType"
                            :module-id="moduleId"
                            @update:scriptConfig="(e) => updateScriptConfig(e, trigger)"
                        />
                    </ideo-form-group>
                </div>
            </div>
        </div>

        <p v-else class="empty">{{ $t('Brak triggerów w tej sekcji.') }}</p>
    </div>
</template>

<style scoped lang="scss">
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  border-bottom: 1px solid #e6e6e6;
  background: linear-gradient(to bottom, #fcfcfc, #f9f9f9);
  font-weight: 600;
  color: #444;
  font-size: 15px;
  border-radius: 6px 6px 0 0;

  h4 {
    margin: 0;
    font-size: 15px;
  }
}

.trigger-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
  background: #fff;

  &:hover {
    background: #f8f8f8;
  }

  .summary {
    font-size: 15px;
    width: 100%;
  }
}

.actions {
    button {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 15px;
        color: #666;
        margin-left: 4px;
        transition: color 0.2s ease;

        &:hover {
            color: #000;
        }
    }
}

.empty {
  padding: 8px 10px;
  font-size: 12px;
  color: #777;
  background: #fff;
}

.add-trigger {
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #444;
  }
}

.delete-trigger i {
  color: #d9534f;
  font-size: 13px;

  &:hover i {
        color: #b52b27;
  }
}
</style>
